import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { History, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AssetHistory({ user, onLogout }) {
  const [history, setHistory] = useState([]);
  const [filter, setFilter] = useState({ asset_id: '', employee_id: '' });
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    fetchHistory();
    fetchAssets();
    fetchEmployees();
  }, []);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filter.asset_id) params.append('asset_id', filter.asset_id);
      if (filter.employee_id) params.append('employee_id', filter.employee_id);
      
      const response = await axios.get(`${API}/assets/history/all?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const fetchAssets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(response.data.assets || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleFilter = () => {
    fetchHistory();
  };

  const clearFilter = () => {
    setFilter({ asset_id: '', employee_id: '' });
    setTimeout(fetchHistory, 100);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Asset History</h1>
          <p className="text-gray-600 mt-2">Track asset allocation and return history</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5" style={{color: '#2b3380'}} />
            <h2 className="text-lg font-bold" style={{color: '#2b3380'}}>Filters</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Filter by Asset</label>
              <select
                value={filter.asset_id}
                onChange={(e) => setFilter({...filter, asset_id: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">All Assets</option>
                {assets.map(asset => (
                  <option key={asset.id} value={asset.id}>
                    {asset.asset_id} - {asset.asset_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Filter by Employee</label>
              <select
                value={filter.employee_id}
                onChange={(e) => setFilter({...filter, employee_id: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">All Employees</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.employee_id} - {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleFilter}
                className="px-6 py-2 rounded-lg text-white font-medium"
                style={{backgroundColor: '#2b3380'}}
              >
                Apply Filter
              </button>
              <button
                onClick={clearFilter}
                className="px-6 py-2 rounded-lg border font-medium"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* History Timeline */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>History Records ({history.length})</h2>
          {history.length > 0 ? (
            <div className="space-y-4">
              {history.map((record, index) => (
                <div key={index} className="border-l-4 pl-4 py-3" style={{borderColor: record.action === 'Allocated' ? '#2b3380' : '#4caf50'}}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{backgroundColor: record.action === 'Allocated' ? '#2b3380' : '#4caf50'}}>
                        <History className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-bold">{record.asset_name}</p>
                        <p className="text-sm text-gray-600">{record.employee_name}</p>
                      </div>
                    </div>
                    <span className="px-3 py-1 rounded-full text-sm font-medium text-white" style={{backgroundColor: record.action === 'Allocated' ? '#2b3380' : '#4caf50'}}>
                      {record.action}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-3">
                    {record.allocation_date && (
                      <div>
                        <span className="text-gray-600">Allocation Date:</span>
                        <p className="font-medium">{new Date(record.allocation_date).toLocaleDateString()}</p>
                      </div>
                    )}
                    {record.return_date && (
                      <div>
                        <span className="text-gray-600">Return Date:</span>
                        <p className="font-medium">{new Date(record.return_date).toLocaleDateString()}</p>
                      </div>
                    )}
                    {record.return_condition && (
                      <div>
                        <span className="text-gray-600">Return Condition:</span>
                        <p className="font-medium">{record.return_condition}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-600">Recorded:</span>
                      <p className="font-medium">{new Date(record.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  {record.notes && (
                    <div className="mt-2">
                      <span className="text-gray-600 text-sm">Notes:</span>
                      <p className="text-sm">{record.notes}</p>
                    </div>
                  )}
                  {record.return_notes && (
                    <div className="mt-2">
                      <span className="text-gray-600 text-sm">Return Notes:</span>
                      <p className="text-sm">{record.return_notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <History className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">No history records found</p>
              <p className="text-sm text-gray-400 mt-1">Asset allocation and return history will appear here</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default AssetHistory;